<template lang="html">
<div>

  <div class="flex justify-between">
    <div class="text-md font-semibold">
      Videos
    </div>
    <div>
      <button class="underline hover:text-blue-500" @click="showUploadClipModal()">Upload a video</button>
    </div>
  </div>

  <div class="flex justify-between items-baseline space-x-2 mt-1">
    <div class="grow w-full">
      <select id="sharedVideoInput" class="w-full form-input" v-model="selectedClip"  v-on:change="loadClip($event)">
        <option value="none" data-timecode="01:00:00:00">Select a video to share...</option>
        <option :value="clip.file" :data-timecode="clip.mtc" :data-framerate="clip.framerate" v-for="clip in clips">{{clip.name}} - {{clip.mtc}} @ {{clip.framerate}}</option>
      </select>
    </div>
    <div>
    @
    </div> 
    <div>{{framerate}}&nbsp;frames</div>
  </div>
  <div class="w-full mt-2 relative h-64 overflow-hidden rounded-md">
        <video class="w-full captions bg-black absolute top-0" controls  id="sharedVideo" muted></video>
  			<div class="bg-black opacity-75 text-white flex space-y-2 flex-col absolute z-10 inset-0 items-center justify-center" v-show="isLoadingClip">
  			<!-- Spinning semi-circle -->
  				<svg class="animate-spin  h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
  					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
  					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  				</svg>
  				<h1>
            Loading clip
  				</h1>
  				</div>	
    <div class="flex items-center justify-between py-2 px-2 top-0 right-0 bg-black text-white absolute z-10">
        <div>
          Starts:  {{ sharedVideoMTC }}
        </div>
      </div>
    <div class="absolute inset-0 flex justify-center items-center bg-gray-200" v-show="selectedClip == 'none'">
      <div class="w-1/2 text-center">
        No video selected. <br />Please select one to begin.
      </div>
    </div>
  </div>
  <div class="flex flex-wrap justify-between items-center mt-2">
    <div class="flex flex-wrap text-xl p-1 pl-2 pr-2 rounded-md" :class="midiIsSetup ? 'bg-black text-white':'bg-red-100 text-red-800 opacity-50'">
      <div id="hours">00</div>
      <div class="px-1">:</div>
      <div id="minutes">00</div>
      <div class="px-1">:</div>
      <div id="seconds">00</div>
      <div class="px-1">:</div>
      <div id="frames">00</div>
      <div id="rate" class="hidden ml-2"></div>
    </div>
    <div class="shrink-0 w-32" v-show="!midiIsSetup">
      <button class="btn btn-red w-full" @click="setupMIDI">
        Connect MIDI
      </button>
    </div>
  </div>
  <hr class="my-4" />
  <div class="grid grid-cols-3 items-center">
    <label class="font-bold" :class="recOnPlay ? 'text-blue-500 neon-shadow-blue' : 'text-gray-500'">
      Record on Play
    </label>
    <div class="col-span-2 flex justify-between items-center">
      <b>
        Bypass Latency comp.
      </b>
      <div class="flex space-x-2 items-center">
        <i class="not-italic leading-none text-3xl" :class="bypassLatencyOn ? 'text-green-500 neon-shadow-green' : 'text-gray-400'">&bullet;</i>
  	    <div class="form-switch green-switch ml-2" @click="onSetBypassLatency">
  				<input type="checkbox" v-model="bypassLatencyOn">
  				<i></i>
  		  </div>
      </div>
    </div>
  </div>
  <hr class="my-4" />
  <div class="mt-2 flex justify-between items-center">
    <div class="text-md font-semibold w-1/2">
      Calibrate talent video
    </div>
    <button class='w-1/2 btn btn-primary duration-9s' id="calibratePeers">
      <span>
        Calibrate talent
       </span>
      </button>
  </div>
  <div class="mt-2 flex justify-between items-center">
    <div class="text-md font-semibold w-1/2">
      PT MTC Video Offset
    </div>
    <select class="form-input w-1/2" v-model="ptLatencyFrames" v-on:change="onPTLatencyChange">
        <option value="0"> 0 frames</option>
        <option value="1"> 1 frame </option>
        <option value="2"> 2 frames </option>
        <option value="3"> 3 frames </option>
        <option value="4"> 4 frames </option>
        <option value="5"> 5 frames (recommended) </option>
        <option value="6"> 6 frames </option>
        <option value="7"> 7 frames </option>
        <option value="8"> 8 frames </option>
        <option value="9"> 9 frames </option>
        <option value="10"> 10 frames </option>
    </select>
  </div>

  <div class="mt-2 flex justify-between items-center">
    <div class="text-md font-semibold w-1/2">
      Virtual device latency comp.
      <div class="mt-1">
        <button class="underline text-gray-500 hover:text-gray-800 text-sm font-light" 
        @click="showAudioRouteCalibrationInstructions = true" v-show="!showAudioRouteCalibrationInstructions">See instructions</button> 
      </div>
    </div>
     <div class="flex space-x-2 w-1/2">
      <select class="form-input flex-grow" v-model="audioRouteLatency" v-on:change="onAudioRouteLatencyChange">
        <option value="0"> 0ms</option>
        <option value="10"> 10ms</option>
        <option value="15"> 15ms</option>
        <option value="20"> 20ms</option>
        <option value="25"> 25ms (most common) </option>
        <option value="35"> 35ms </option>
        <option value="50"> 50ms </option>
        <option value="75"> 75ms </option>
        <option value="100"> 100 ms </option>
        <option value="125"> 125 ms </option>
        <option value="150"> 150ms </option>
        <option value="175"> 175ms </option>
        <option value="200"> 200ms  </option>
      </select>
            <button class="btn btn-secondary w-32" @click="playBeepSound"> Beep </button>
     </div>
  </div>
<ol class="list-decimal space-y-2" v-show="showAudioRouteCalibrationInstructions">
          <li> Select in the "From Guests" selector the lowest latency possible audio route (usually built-in speakers).  
                Note that for now, you may need to toggle the selector to "none" and back to the usual route to make it work.
          </li>
          <li> Next, the "From Talent 1" should be set to the usual route where it will be recorded, 
                but set it up in a way you can listen thru the built in speakers (same as "from guests") that audio. 
          </li>
          <li> 
            Click the "beep" button and listen to the beep. Adjust the selector below until you only hear one beep.

            Note that the clock is not always 100% consistent, just click it a couple of times to ensure the calibration is ok.
          </li>
        </ol>
  <div class="mt-2 flex justify-between items-center">
    <div class="text-md font-semibold w-1/2">
        Extra latency
      </div>
      <select class="form-input w-1/2" v-model="talentLatency" v-on:change="onTalentLatencyChange">
        <option value="-100"> -100ms </option>
        <option value="-75"> -75ms </option>
        <option value="-50"> -50ms </option>
        <option value="-25"> -25ms </option>
        <option value="0"> 0ms (recommended)</option>
        <option value="25"> 25ms </option>
        <option value="50"> 50ms </option>
        <option value="75"> 75ms </option>
        <option value="100"> 100ms</option>
        <option value="125"> 125ms </option>
        <option value="150"> 150ms </option>
        <option value="175"> 175ms </option>
        <option value="200"> 200ms  </option>
      </select>
  </div>
  <div class="stats hidden">
    <hr class="mt-10"/>
    <div class="text-lg font-bold py-2"> Stats for nerds </div>
    <hr />
    <table class="w-full text-left">
      <tbody>
        <tr class="border-t-2">
          <th class="p-1">Messages per Second</th>
          <td class="text-right" id="messagesPerSecond">0</td>
                </tr>
                <tr class="border-t-2">
                    <th class="p-1">Frames per Second</th>
                    <td class="text-right" id="framesPerSecond">0</td>
                </tr>
                <tr class="border-t-2">
                    <th class="p-1">MMC recieved</th>
                    <td class="text-right" id="MMCmessages">0</td>
                </tr>
                <tr class="border-t-2">
                    <th class="p-1">MTC recieved</th>
                    <td class="text-right" id="MTCmessages">0</td>
                </tr>
                <tr class="border-t-2">
                    <th class="p-1">Duplicate messages avoided</th>
                    <td class="text-right" id="duplicateMessages">0</td>
                </tr>
                <tr class="border-t">
                    <th class="p-1">Jumps</th>
                    <td id="jumpsRecorded" class="text-right">0</td>
                </tr>
                <tr class="border-t">
                    <th class="p-1">Cause of Jump</th>
                    <td id="causeOfJump" class="text-right"></td>
                </tr>
            </tbody>
        </table>
    </div>
    <div id="thumbnails"></div>
    <modal :showModal="showUploadModal" @close="showUploadModal = false">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <!-- Upload cloud icon -->
            <svg class="h-6 w-6 text-green-600 fill-current" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                 viewBox="0 0 24 24">
                <path fill-rule="evenodd"
                      d="M11 4C8.79082 4 7 5.79085 7 8C7 8.03242 7.00047 8.06627 7.00131 8.10224C7.01219 8.56727 6.70099 8.97839 6.25047 9.09416C4.95577 9.42685 4 10.6031 4 12C4 13.6569 5.34317 15 7 15H8C8.55228 15 9 15.4477 9 16C9 16.5523 8.55228 17 8 17H7C4.23861 17 2 14.7614 2 12C2 9.93746 3.2482 8.16845 5.02926 7.40373C5.32856 4.36995 7.88746 2 11 2C13.2236 2 15.1629 3.20934 16.199 5.00324C19.4207 5.10823 22 7.75289 22 11C22 14.3137 19.3138 17 16 17C15.4477 17 15 16.5523 15 16C15 15.4477 15.4477 15 16 15C18.2092 15 20 13.2091 20 11C20 8.79085 18.2092 7 16 7C15.8893 7 15.78 7.00447 15.6718 7.01322C15.2449 7.04776 14.8434 6.8066 14.6734 6.4135C14.0584 4.99174 12.6439 4 11 4ZM11.2929 9.29289C11.6834 8.90237 12.3166 8.90237 12.7071 9.29289L14.7071 11.2929C15.0976 11.6834 15.0976 12.3166 14.7071 12.7071C14.3166 13.0976 13.6834 13.0976 13.2929 12.7071L13 12.4142V20C13 20.5523 12.5523 21 12 21C11.4477 21 11 20.5523 11 20V12.4142L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071C8.90237 12.3166 8.90237 11.6834 9.29289 11.2929L11.2929 9.29289Z"
                      clip-rule="evenodd"/>
            </svg>
        </div>
        <div class="mt-3 text-center sm:mt-5">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                Upload a new clip
            </h3>
            <div class="mt-2">
                <p class="text-sm text-gray-500">
                    Upload a new clip to the folder.  The new clip will be updated automatically.
                </p>
            </div>
        </div>
        <div class="mt-5 space-y-2">
            <label class="font-bold" for="new_clip_name">Name</label>
            <input id="new_clip_name" type="text" class="form-input" placeholder="Soccer match clip 1" v-model="new_clip_name">

            <fieldset class="grid grid-cols-2 gap-4">
                <div>
                    <label class="font-bold" for="new_clip_mtc">MTC</label>
                    <input id="new_clip_mtc" type="text" class="form-input" placeholder="00:00:00:00" v-model="new_clip_mtc">
                </div>
                <div>
                    <label class="font-bold" for="new_clip_framerate">Framerate</label>
                    <input id="new_clip_framerate" type="text" class="form-input" placeholder="24" v-model="new_clip_framerate">
                </div>
            </fieldset>
            <label class="font-bold mt-2">Upload a file</label>
            <span>
                Please, upload files smaller than {{ fileLimitInMB }}MB to ensure a good experience.
            </span>
            <input type="file" ref="user_upload" accept="video/*" class="form-input" id="new_clip_blob"
                   v-on:change="handleFileUploaded($event)">

            <label class="font-bold mt-4" v-show="preview_upload">Preview</label>
            <video :src="preview_upload" class="w-1/2 mx-auto rounded-md" v-show="preview_upload"/>
            <span v-show="fileSizeInvalid" class="text-red-600">The file you selected is larger than {{ fileLimitInMB }}MB. </span>
        </div>
        <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
            <button type="button" class="btn sm:col-start-2 sm:text-sm" @click="uploadClip"
                    :class="{'btn-primary': uploadClipValidations, 'btn-disabled': !uploadClipValidations}"
                    :disabled="!uploadClipValidations">
                Upload clip
            </button>
            <button type="button" class="btn btn-secondary"
                    @click="showUploadModal = false;">
                Cancel
            </button>
        </div>
    </modal>
</div>
</template>
<script>

import axios from 'axios';
import { parseStringToTimecode, timecodeToString} from 'anywhere/MIDI/timecodeUtils'
import { MasterPlayback } from 'anywhere/MIDI/remotePlayback';

import MIDIReceiver from 'anywhere/MIDI/MIDIReceiver';
import commandsManager from 'general/CommandsManager'
import { attachSinkId } from '../webaudio/utils';

import { generateThumbnails } from 'utils/thumbnails';
import preloader from 'utils/video-preloader';
import {addTask} from 'utils/status-indicator';

export default {
  data() {
    return {
      clips: [],
      midiIsSetup: false,
      sharedVideoMTC: null,
      sharedVideoURL: null,
      framerate: 24,
      showUploadModal: false,
      selectedClip: "none",

      new_clip_name: '',
      new_clip_mtc: "00:00:00:00",
      new_clip_framerate: 30,
      new_clip_folder_name: '',
      fileSizeInvalid: false,
      fileLimitInMB: 200,
      preview_upload: null,
      formData: {
        type: FormData
      },
      uploadTask: {},
      isLoading: false,

      playing: false,
      ptLatencyFrames: 5, 
      showAudioRouteCalibrationInstructions: false,
      audioRouteLatency: 25,  // this was measured to be 200ms on Alonsos machine
      talentLatency: 0,  // used as "extra" latency
      playbackManager: null,
      receiver: null,

      isLoadingClip: false,
      recOnPlay: false,
      isRecording: false,
      recordingStartedAt: null,

      bypassLatencyOn: false,
    }
  },
  methods: {
    async setupMIDI() {
      let receiver = new MIDIReceiver(
        (action, timecode)=>{
          switch(action){
          case "MTC":
            this.updateTimecodeUI(timecode);
            break;
          case "GoTo":
            this.playbackManager.jumpToTimecode(timecode);
            this.updateTimecodeUI(timecode);
            break;
          case "REC_ON":
            this.recOnPlay = true;
            break;
          case "REC_OFF":
            window.setTimeout(()=>{
              this.recOnPlay = false;
            }, 1000)
            break;
          case "Play":
              this.playing = true;
              this.playbackManager.jumpToTimecode(timecode);
              this.playbackManager.play();
              this.updateTimecodeUI(timecode);
              if (this.recOnPlay && this.recordingStartedAt == null){
                this.isRecording = true;
                this.recordingStartedAt = new Date();
                manager.toggleRecording();
              }
              break;
            case "Stop":
              this.playing = false;
              // theres a minimum delay to stop the recording to avoid a bug
              // where "ghost" recordings are pushed forward
              if (this.isRecording && new Date () - this.recordingStartedAt > 350){
                manager.toggleRecording();
                this.recordingStartedAt = null;
                this.isRecording = false;
              }
              this.playbackManager.pause();
              this.updateTimecodeUI(timecode);
              break;
          }
        }
      );
      this.receiver = receiver;
      this.receiver.setCurrentFramerate(this.framerate);
      navigator.requestMIDIAccess({ sysex: true, software: true })
               .then((access) => {
                 // Get lists of available MIDI controllers
                 access.inputs.forEach((midi) => {
                   if (midi.name !== 'From MicTap') return;
                   midi.onmidimessage = receiver.HandleRawMIDIData.bind(receiver);
                   this.midiIsSetup = true
                 });
                 access.onstatechange = (e) => {
                   if (e.port.name !== 'From MicTap') return;
                   if (e.port.state === 'connected') {
                     this.midiIsSetup = true
                     e.port.onmidimessage = receiver.HandleRawMIDIData.bind(receiver);
                   }
                   if (e.port.state === 'disconnected'){
                     this.midiIsSetup = false
                   }
                 };
                 if (this.midiIsSetup == false) {
                   this.openMIDIReader();
                 }
               });
    },
    showUploadClipModal(){
      // ensure that the array is regenerated
      this.formData = new FormData();
      this.showUploadModal = true;
    },
    getSharedVideoInfo(){
      return { url: this.sharedVideoURL }
    },
    loadClip(event){
      if (this.selectedClip == 'none') {
        // remove all video info
        $('#sharedVideo')[0].src = "";
        let mtc = "01:00:00:00"
        let framerate = 24
        this.framerate = framerate;
        if (this.receiver) this.receiver.setCurrentFramerate(this.framerate);
        this.sharedVideoMTC = mtc;
        this.sharedVideoURL = "";
        commandsManager.sendAllCommand('loadSharedVideo', this.getSharedVideoInfo());
        return;
      }
      this.isLoadingClip = true;
      preloader(this.selectedClip, (progress, loaded, total)=>{}).then((blob) => {
        // video is loaded
        let url = URL.createObjectURL(blob);
        $('#sharedVideo')[0].src = url
        // wait to load clip
        generateThumbnails('sharedVideo', 'thumbnails')
          .then(() => {
            let selector = $(event.target).children('option:selected')
            let mtc = selector.data('timecode');
            let framerate = selector.data('framerate');
            this.framerate = framerate;
            if (this.receiver) this.receiver.setCurrentFramerate(this.framerate);
            this.sharedVideoMTC = mtc;
            this.sharedVideoURL = event.target.value;
            const timecode = parseStringToTimecode(mtc, framerate);
            this.playbackManager.setBaseTimecode(timecode);

            this.isLoadingClip = false;
            const videoEl = document.querySelector("#sharedVideo");
            if (videoEl && videoEl.duration > 0) {
              videoEl.currentTime = 0 + this.ptLatencyFrames / this.framerate;
              this.playbackManager.setClipDuration(videoEl.duration);
            } else {
              alert("No duration set yikes")
            }

            // this.playbackManager.setLocalVideoListeners();
          })
      }).catch((err)=>{
        this.isLoadingClip = false;
        console.log("error loading clip", err)
        alert("Select another clip")
      })
      // tell everyone to load the video
      commandsManager.sendAllCommand('loadSharedVideo', {url: this.selectedClip});

    },
    loadClipList(){
      this.isLoading = true;
      const headers = {'Accept': 'application/json'};
      axios.get('/library/video_clips', {headers}).then(response => {
        this.clips = response.data;
        this.isLoading = false;
      }).catch(err => {
        this.clips = [];
        this.isLoading = false;
      });
    },
    uploadClip() {
      // validations
      if (!this.uploadClipValidations) return;

      this.uploadTask = addTask({id: 'UPLOAD_CLIP',
        determinate: true,
        message: 'Uploading clip...'
      })

      let file = this.$refs.user_upload.files[0];
      if (this.fileSizeIsValid(file) == false) return;

      let url = "/library/upload"

      let formData = new FormData();
      formData.append('authenticity_token', authenticity_token);
      formData.append('video_clip[name]', this.new_clip_name);
      formData.append('video_clip[mtc]', this.new_clip_mtc);
      formData.append('video_clip[framerate]', this.new_clip_framerate);
      formData.append('video_clip[file]', file);

      axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        },
        onUploadProgress: progressEvent => {
          if (progressEvent.lengthComputable) {
            this.uploadTask.update(parseInt(progressEvent.loaded / progressEvent.total, 10) * 100);
          }
          if ((progressEvent.loaded / progressEvent.total) === 1) {
            // finished upload
          }
        }
      })
      .then(response => {
          this.showUploadModal = false;
          this.new_clip_name = ''
          this.new_clip_mtc = "00:00:00:00"
          this.new_clip_framerate = 30
          this.new_clip_folder_name = ''
          this.loadClipList();

          this.uploadTask.done();
      }).catch(err => {
        this.uploadTask.error();
      });
    },
    fileSizeIsValid(file) {
      return (file.size / 1024) / 1024 <= this.fileLimitInMB;
    },
    // show a preview of image
    handleFileUploaded(event) {
      if (event.target.files.length == 0) {
        // remove picture
        this.preview_upload = null;
        return;
      }
      let file = event.target.files[0];

      this.fileSizeInvalid = !this.fileSizeIsValid(file);
      if (this.fileSizeInvalid) {
        this.preview_upload = null;
        return;
      }

      let previewURL = URL.createObjectURL(file);
      this.preview_upload = previewURL;
    },
    onPTLatencyChange(){
      this.ptLatencyFrames = parseInt(this.ptLatencyFrames, 10);
      this.playbackManager.setPTLatency(this.ptLatencyFrames / this.framerate)

      // remember the latency on localstorage
      localStorage.setItem('mt_pt_latency_frames', this.ptLatencyFrames);
    },
    playBeepSound(){
      window.setTimeout(()=>{
        let switchSound = document.getElementById("audioRouteCalibrationSound2");
        switchSound.volume = 1.0;
        switchSound.play()
      }, this.audioRouteLatency)
      // play beep sound on both the default audio speakers and via the selected  "talent" output
      // attach this beep sound to the talent output

      const talentDestination1 = $(".audioOutput[data-id='1']").val();
      attachSinkId("Calibration Sound", document.getElementById("audioRouteCalibrationSound"), talentDestination1)
      document.getElementById("audioRouteCalibrationSound").play();
    },
    onAudioRouteLatencyChange() {
      // set the new latency on the midi
      this.playbackManager.setAudioRouteLatency(this.audioRouteLatency/1000)

      // remember the latency on localstorage
      localStorage.setItem('mt_audio_route_latency', this.audioRouteLatency);
    },
    onTalentLatencyChange() {
      // set the new latency on the midi
      this.playbackManager.setTalentLatency(this.talentLatency/1000)
    },
    openMIDIReader() {
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = 'mictap://open-mictap-midi-reader';
      a.setAttribute('launch', 'launch');
      a.innerHTML = 'CLICK ME';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
      }, 100);
    },
    updateTimecodeUI(timecode) {
      $('#hours').html(`${timecode.hours}`.padStart(2, '0'));
      $('#minutes').html(`${timecode.minutes}`.padStart(2, '0'));
      $('#seconds').html(`${timecode.seconds}`.padStart(2, '0'));
      $('#frames').html(`${timecode.frames}`.padStart(2, '0'));
      $('#rate').html(`${timecode.framerate} fps`);
    },
    onSetBypassLatency(){
      this.bypassLatencyOn = !this.bypassLatencyOn;
      this.playbackManager.setBypassLatency(this.bypassLatencyOn);
    }
  },

  mounted() {
    this.loadClipList();
    this.playbackManager = MasterPlayback.getInstance(commandsManager);
    // TODO: Set up MIDI?

    // set the latencies in the playback manager
    this.playbackManager.setPTLatency(this.ptLatencyFrames / this.framerate)
    this.playbackManager.setAudioRouteLatency(this.audioRouteLatency/1000); 
    this.playbackManager.setTalentLatency(this.talentLatency/1000); 

    // retrieve from local storage
    let ptLatencyFrames = localStorage.getItem('mt_pt_latency_frames');
    if (ptLatencyFrames) {
      this.ptLatencyFrames = parseInt(ptLatencyFrames, 10);
    }
    let audioRouteLatency = localStorage.getItem('mt_audio_route_latency');
    if (audioRouteLatency) {
      this.audioRouteLatency = parseInt(audioRouteLatency, 10);
    }
  },
  computed: {
    uploadClipValidations() {
      // if this.preview_upload is set it means that a file is loaded
      return this.new_clip_name.length > 0 && this.preview_upload && this.selected_clip != "none" && this.fileSizeInvalid == false;
    },
    },
}
</script>
<style lang="sass">
.neon-shadow-blue{
  text-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #fff, 0 0 10px #0000ff;
}
.neon-shadow-green{
  text-shadow: 0 0 10px #fff, 0 0 20px #fff, 0 0 30px #fff, 0 0 10px #00ff00;
}

</style>
